import { DraftFormWithAiButton } from "@/features/ai-builder/DraftFormWithAiButton";
import { BuilderAddOption } from "@/features/builder/components/BuilderAddOption";
import { filterClinicianBuilderRows } from "@/features/builder/utils/builderAccess";
import { canAddAttribute, useMyAccessModes } from "@/features/clinical-access/api/selectMyAccessModes";
import { selectFormsQueryOptions } from "@/features/forms/api/selectForms";
import { useCreateForm } from "@/features/forms/api/formBuilderApi";
import { useFormValidationStatusesQuery } from "@/features/validations/api/selectValidationStatus";
import ValidationStatusBadge from "@/features/validations/components/ValidationStatusBadge";
import InviteToValidateDialog from "@/features/validations/components/InviteToValidateDialog";
import ValidationHistoryDialog from "@/features/validations/components/ValidationHistoryDialog";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { FormViewT } from "../@patient-watch/types";
import * as React from "react";
import {
    type ColumnDef,
    type ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
    type VisibilityState,
} from "@tanstack/react-table";
import {
    ArrowUpDown,
    CheckCircle2,
    ChevronDown,
    ExternalLink,
    MoreHorizontal,
    Pencil,
    Plus,
    Settings,
    XCircle,
} from "lucide-react";
import { FormBadge, FormPrevalenceBadge, FormScoringBadge, FormStatusBadge, ui } from "@patient-watch/ui";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@patient-watch/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { useAuth } from "@/utils/auth";

function FormValidationStatusCell({ formId }: { formId: string }) {
    const { data: statuses } = useFormValidationStatusesQuery();
    return <ValidationStatusBadge status={statuses?.[formId]} />;
}

export const columns: ColumnDef<FormViewT>[] = [
    {
        accessorKey: "form_name",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    size="sm"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Form Name
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                </ui.Button>
            );
        },
        cell: ({ row }) => (
            <div className="min-w-0 max-w-[14rem] font-medium truncate" title={String(row.getValue("form_name") ?? "")}>
                {row.getValue("form_name")}
            </div>
        ),
    },
    {
        accessorKey: "form_description",
        header: "Description",
        cell: ({ row }) => (
            <div
                className="min-w-0 max-w-[12rem] text-muted-foreground truncate"
                title={String(row.getValue("form_description") ?? "")}
            >
                {row.getValue("form_description")}
            </div>
        ),
    },
    {
        accessorKey: "status",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    size="sm"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Status
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                </ui.Button>
            );
        },
        cell: ({ row }) => {
            const status = row.getValue("status") as string;
            return <FormStatusBadge status={status} />;
        },
    },
    {
        accessorKey: "is_published",
        header: "Published",
        cell: ({ row }) => {
            const isPublished = row.getValue("is_published") as boolean;
            return isPublished ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
                <XCircle className="w-4 h-4 text-gray-400" />
            );
        },
    },
    {
        accessorKey: "number_of_pages",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    size="sm"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Pages
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                </ui.Button>
            );
        },
        cell: ({ row }) => <div className="text-center">{row.getValue("number_of_pages")}</div>,
    },
    {
        accessorKey: "number_of_questions",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    size="sm"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Questions
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                </ui.Button>
            );
        },
        cell: ({ row }) => <div className="text-center">{row.getValue("number_of_questions")}</div>,
    },
    {
        accessorKey: "time_to_complete",
        header: ({ column }) => {
            return (
                <ui.Button
                    variant="ghost"
                    size="sm"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Time (min)
                    <ArrowUpDown className="ml-1 w-3 h-3" />
                </ui.Button>
            );
        },
        cell: ({ row }) => <div className="text-center">{row.getValue("time_to_complete")}</div>,
    },
    {
        accessorKey: "medical_specialties",
        header: "Specialties",
        cell: ({ row }) => {
            const specialties = row.getValue("medical_specialties") as string[];
            if (!specialties || specialties.length === 0) {
                return <span className="text-muted-foreground"> - </span>;
            }
            return (
                <div className="flex flex-wrap gap-1">
                    {specialties.slice(0, 2).map((specialty) => (
                        <FormBadge key={specialty} variant="specialty">
                            {specialty.replace(/_/g, " ")}
                        </FormBadge>
                    ))}
                    {specialties.length > 2 && <FormBadge variant="specialty">+{specialties.length - 2}</FormBadge>}
                </div>
            );
        },
    },
    {
        accessorKey: "prevalence",
        header: "Prevalence",
        cell: ({ row }) => {
            const prevalence = row.getValue("prevalence") as string;
            return prevalence ? (
                <FormPrevalenceBadge prevalence={prevalence} />
            ) : (
                <span className="text-muted-foreground"> - </span>
            );
        },
    },
    {
        accessorKey: "is_licensed",
        header: "Licensed",
        cell: ({ row }) => {
            const isLicensed = row.getValue("is_licensed") as boolean;
            return isLicensed ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
                <XCircle className="w-4 h-4 text-gray-400" />
            );
        },
    },
    {
        accessorKey: "scoring",
        header: "Scoring",
        cell: ({ row }) => {
            const scoring = row.getValue("scoring") as string;
            return scoring ? (
                <FormScoringBadge scoring={scoring} />
            ) : (
                <span className="text-muted-foreground"> - </span>
            );
        },
    },
    {
        id: "validation",
        header: "Validation",
        cell: ({ row }) => <FormValidationStatusCell formId={row.original.form_id ?? ""} />,
    },
    {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
            const form = row.original;
            const formId = form.form_id || "";

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <ui.Button variant="ghost" size="icon" className="w-8 h-8 shrink-0" aria-label="Form actions">
                            <MoreHorizontal className="w-4 h-4" />
                        </ui.Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                        <DropdownMenuItem asChild>
                            <Link
                                to="/forms/$formId"
                                target="_blank"
                                params={{ formId }}
                                className="flex items-center gap-2 cursor-pointer"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Test form
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link
                                to="/builder/forms/$formId"
                                params={{ formId }}
                                className="flex items-center gap-2 cursor-pointer"
                            >
                                <Pencil className="w-4 h-4" />
                                Open builder
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link
                                to="/builder/forms/$formId/metadata"
                                params={{ formId }}
                                className="flex items-center gap-2 cursor-pointer"
                            >
                                <Settings className="w-4 h-4" />
                                Metadata
                            </Link>
                        </DropdownMenuItem>
                        <InviteToValidateDialog entityType="form" entityId={formId} />
                        <ValidationHistoryDialog entityType="form" entityId={formId} />
                    </DropdownMenuContent>
                </DropdownMenu>
            );
        },
    },
];

function FormTemplatesPage() {
    const auth = useAuth();
    const { forms: loaderForms } = Route.useLoaderData();
    const { data: queriedForms } = useQuery({
        ...selectFormsQueryOptions(),
        initialData: loaderForms,
        refetchOnMount: true,
    });
    const isAdmin = auth.roles.isAdmin;
    const userId = auth.id;
    const forms = React.useMemo(
        () => filterClinicianBuilderRows((queriedForms ?? loaderForms) as FormViewT[], { isAdmin, userId }),
        [queriedForms, loaderForms, isAdmin, userId]
    );
    const navigate = useNavigate();
    const createForm = useCreateForm();
    const { data: accessModes } = useMyAccessModes();
    const canAddForms = isAdmin || canAddAttribute(accessModes, "forms");
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
        medical_specialties: false,
        prevalence: false,
        is_licensed: false,
        scoring: false,
    });
    const [rowSelection, setRowSelection] = React.useState({});

    const table = useReactTable({
        data: forms,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
    });

    return (
        <div className="p-4 w-full min-w-0 max-w-full">
            <div className="flex justify-between items-center gap-3 mb-4">
                <div className="flex flex-1 items-center gap-2 min-w-0">
                    <ui.Input
                        placeholder="Filter forms..."
                        value={(table.getColumn("form_name")?.getFilterValue() as string) ?? ""}
                        onChange={(event) => table.getColumn("form_name")?.setFilterValue(event.target.value)}
                        className="max-w-sm"
                    />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <ui.Button size="sm" variant="outline">
                                Columns <ChevronDown />
                            </ui.Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {table
                                .getAllColumns()
                                .filter((column) => column.getCanHide())
                                .map((column) => {
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={column.id}
                                            className="capitalize"
                                            checked={column.getIsVisible()}
                                            onCheckedChange={(value) => column.toggleVisibility(!!value)}
                                        >
                                            {column.id.replace(/_/g, " ")}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                {canAddForms ? (
                    <div className="flex shrink-0 items-center gap-2">
                        <DraftFormWithAiButton />
                        <ui.Button
                            size="sm"
                            disabled={createForm.isPending}
                            onClick={() => {
                                createForm.mutate(undefined, {
                                    onSuccess: ({ formId }) => {
                                        navigate({
                                            to: "/builder/forms/$formId",
                                            params: { formId },
                                        });
                                    },
                                });
                            }}
                        >
                            <Plus className="w-4 h-4" />
                            New form
                        </ui.Button>
                    </div>
                ) : (
                    <BuilderAddOption kind="form" />
                )}
            </div>
            <div className="bg-white dark:bg-slate-900 border rounded-md min-w-0 overflow-hidden">
                <Table className="w-full table-fixed">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const id = header.column.id;
                                    const headClass =
                                        id === "actions"
                                            ? "w-16 pr-3"
                                            : id === "form_name"
                                              ? "w-[min(14rem,22%)]"
                                              : id === "form_description"
                                                ? "w-[min(12rem,18%)]"
                                                : undefined;
                                    return (
                                        <TableHead key={header.id} className={headClass}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            className={cell.column.id === "actions" ? "pr-3" : undefined}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center py-4">
                <div className="text-muted-foreground text-sm">
                    {table.getFilteredRowModel().rows.length} form(s) total
                </div>
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_app/builder/forms/")({
    component: FormTemplatesPage,
    loader: async ({ context }) => {
        const forms = await context?.queryClient.ensureQueryData(selectFormsQueryOptions());
        return { forms: forms as FormViewT[] };
    },
});
